import { useState, useEffect } from "react";
import { http } from "@/lib/api";
import Layout from "@/components/Layout";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Clock, MapPin, Video, Calendar, User, Mail, Phone, Loader2 } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

const STEPS = ["Service", "Format", "Date & Time", "Your Info", "Confirm"];

export default function BookingPage() {
  useSEO("booking");
  const [searchParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);

  // Booking state
  const [selectedService, setSelectedService] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [form, setForm] = useState({ client_name: "", client_email: "", client_phone: "", preferred_name: "", referral_source: "", reason_for_booking: "", hopes: "", accessibility_needs: "", client_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Check for success/cancelled return from Stripe
  const bookingStatus = searchParams.get("status");
  const bookingId = searchParams.get("id");

  useEffect(() => {
    Promise.all([
      http.get("/booking/public/settings"),
      http.get("/booking/public/services"),
    ]).then(([s, svc]) => {
      setSettings(s.data);
      setServices(svc.data);
    }).catch(() => toast.error("Could not load booking."))
      .finally(() => setLoading(false));
  }, []);

  // Load slots when date changes
  useEffect(() => {
    if (!selectedService || !selectedDate || !selectedFormat) return;
    setSlotsLoading(true);
    setSelectedSlot(null);
    http.get(`/booking/public/available-slots?service_id=${selectedService.id}&date=${selectedDate}&format=${selectedFormat}`)
      .then(r => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [selectedService, selectedDate, selectedFormat]);

  if (loading) return <Layout><div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center"><Loader2 className="text-[#D4AF37] animate-spin" /></div></Layout>;

  // Success/cancel return screens
  if (bookingStatus === "success") return (
    <Layout>
      <PageWrapper>
        <div className="max-w-2xl mx-auto text-center py-20">
          <div className="w-16 h-16 mx-auto rounded-full border border-[#D4AF37]/30 flex items-center justify-center mb-6"><Check size={28} className="text-[#D4AF37]" /></div>
          <h1 className="font-mc-h text-4xl text-[#F5F5F0] mb-4">Your seat is confirmed.</h1>
          <p className="font-mc-b text-base text-zinc-400 mb-8">Your payment was received. Check your email for confirmation details.</p>
          <Link to="/mirror-conversations" className="text-[#D4AF37] font-mc-b text-sm underline underline-offset-4">Back to Mirror Conversations</Link>
        </div>
      </PageWrapper>
    </Layout>
  );

  if (bookingStatus === "cancelled") return (
    <Layout>
      <PageWrapper>
        <div className="max-w-2xl mx-auto text-center py-20">
          <h1 className="font-mc-h text-3xl text-[#F5F5F0] mb-4">Payment was not completed.</h1>
          <p className="font-mc-b text-base text-zinc-400 mb-8">Your booking is still reserved. You can try again.</p>
          <Link to="/book" className="border border-[#D4AF37] text-[#D4AF37] px-8 py-3 rounded-full font-mc-b text-sm uppercase hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-all">Try Again</Link>
        </div>
      </PageWrapper>
    </Layout>
  );

  // Booking complete (no payment needed)
  if (result) return (
    <Layout>
      <PageWrapper>
        <div className="max-w-2xl mx-auto text-center py-20">
          <div className="w-16 h-16 mx-auto rounded-full border border-[#D4AF37]/30 flex items-center justify-center mb-6"><Check size={28} className="text-[#D4AF37]" /></div>
          <h1 className="font-mc-h text-4xl text-[#F5F5F0] mb-4">Your seat is reserved.</h1>
          <p className="font-mc-b text-base text-zinc-400 mb-2">{result.message}</p>
          <p className="font-mc-b text-sm text-zinc-600 mb-8">Check your email for details.</p>
          <Link to="/mirror-conversations" className="text-[#D4AF37] font-mc-b text-sm underline underline-offset-4">Back to Mirror Conversations</Link>
        </div>
      </PageWrapper>
    </Layout>
  );

  // Auto-skip format step if only one option
  const handleServiceSelect = (svc) => {
    setSelectedService(svc);
    const hasOffice = svc.in_office_enabled;
    const hasZoom = svc.zoom_enabled && settings?.zoom_enabled_global;
    if (hasOffice && !hasZoom) { setSelectedFormat("office"); setStep(2); }
    else if (!hasOffice && hasZoom) { setSelectedFormat("zoom"); setStep(2); }
    else { setStep(1); }
  };

  const handleSubmit = async () => {
    if (!form.client_name || !form.client_email) { toast.error("Name and email are required."); return; }
    setSubmitting(true);
    try {
      const { data } = await http.post("/booking/public/book", {
        service_id: selectedService.id,
        format: selectedFormat,
        date: selectedDate,
        start_time: selectedSlot.start_time,
        ...form,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setResult(data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed.");
      setSubmitting(false);
    }
  };

  // Generate date options (next N days)
  const maxDays = settings?.max_advance_booking_days || 60;
  const dateOptions = [];
  const today = new Date();
  for (let i = 1; i <= maxDays; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    const dow = d.getDay();
    // Only show weekdays for now (availability will filter further)
    dateOptions.push({ value: d.toISOString().split("T")[0], label: d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }), dow });
  }

  return (
    <Layout>
      <PageWrapper>
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="font-mc-b text-xs tracking-[0.3em] uppercase text-[#D4AF37] mb-3">Mirror Conversations&trade;</p>
            <h1 className="font-mc-h text-3xl md:text-5xl font-light text-[#F5F5F0] mb-3">{settings?.booking_page_headline}</h1>
            <p className="font-mc-b text-base text-zinc-400">{settings?.booking_page_subheadline}</p>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-1 mb-12">
            {STEPS.map((s, i) => (
              <div key={i} className="flex items-center gap-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-mc-b text-xs transition-colors ${i <= step ? "bg-[#D4AF37] text-[#0A0A0A]" : "border border-zinc-700 text-zinc-600"}`}>
                  {i < step ? <Check size={14} /> : i + 1}
                </div>
                {i < STEPS.length - 1 && <div className={`w-8 h-px ${i < step ? "bg-[#D4AF37]" : "bg-zinc-800"}`} />}
              </div>
            ))}
          </div>

          {/* Step Content */}
          <AnimatePresence mode="wait">
            {step === 0 && (
              <StepContainer key="s0">
                <h2 className="font-mc-h text-2xl text-[#F5F5F0] mb-6 text-center">Choose Your Conversation</h2>
                <div className="space-y-4">
                  {services.map(svc => (
                    <button key={svc.id} onClick={() => handleServiceSelect(svc)} className="w-full text-left rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 hover:border-[#D4AF37]/40 transition-all group" data-testid={`service-${svc.id}`}>
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-mc-h text-xl text-[#F5F5F0] mb-1">{svc.title}</h3>
                          <p className="font-mc-b text-sm text-zinc-500 mb-2">{svc.short_desc}</p>
                          <div className="flex gap-4 text-xs text-zinc-600">
                            <span className="flex items-center gap-1"><Clock size={12} /> {svc.duration} min</span>
                            {svc.in_office_enabled && <span className="flex items-center gap-1"><MapPin size={12} /> In Office</span>}
                            {svc.zoom_enabled && settings?.zoom_enabled_global && <span className="flex items-center gap-1"><Video size={12} /> Zoom</span>}
                          </div>
                        </div>
                        {svc.price > 0 && <span className="font-mc-h text-2xl text-[#D4AF37] shrink-0">${svc.price}</span>}
                        {svc.price === 0 && <span className="font-mc-b text-xs text-[#D4AF37] uppercase tracking-wider">Free</span>}
                      </div>
                    </button>
                  ))}
                </div>
                {services.length === 0 && <p className="text-center text-zinc-600 font-mc-b">No services available right now.</p>}
              </StepContainer>
            )}

            {step === 1 && (
              <StepContainer key="s1">
                <h2 className="font-mc-h text-2xl text-[#F5F5F0] mb-6 text-center">How Would You Like to Meet?</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <button onClick={() => { setSelectedFormat("office"); setStep(2); }} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-8 hover:border-[#D4AF37]/40 transition-all text-center" data-testid="format-office">
                    <MapPin size={32} className="text-[#D4AF37] mx-auto mb-4" />
                    <h3 className="font-mc-h text-xl text-[#F5F5F0] mb-2">In Office</h3>
                    <p className="font-mc-b text-xs text-zinc-500 leading-relaxed">{settings?.office_address?.split("\n")[0]}</p>
                  </button>
                  <button onClick={() => { setSelectedFormat("zoom"); setStep(2); }} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-8 hover:border-[#D4AF37]/40 transition-all text-center" data-testid="format-zoom">
                    <Video size={32} className="text-[#D4AF37] mx-auto mb-4" />
                    <h3 className="font-mc-h text-xl text-[#F5F5F0] mb-2">Zoom</h3>
                    <p className="font-mc-b text-xs text-zinc-500">Join from anywhere</p>
                  </button>
                </div>
                <BackBtn onClick={() => setStep(0)} />
              </StepContainer>
            )}

            {step === 2 && (
              <StepContainer key="s2">
                <h2 className="font-mc-h text-2xl text-[#F5F5F0] mb-6 text-center">Pick a Date & Time</h2>
                {/* Date picker */}
                <div className="mb-6">
                  <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-500 mb-3 block">Select a Date</label>
                  <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto pr-2">
                    {dateOptions.map(d => (
                      <button key={d.value} onClick={() => setSelectedDate(d.value)} className={`px-4 py-2 rounded-lg font-mc-b text-xs transition-all ${selectedDate === d.value ? "bg-[#D4AF37] text-[#0A0A0A]" : "border border-white/10 text-zinc-500 hover:border-[#D4AF37]/30"}`} data-testid={`date-${d.value}`}>
                        {d.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Time slots */}
                {selectedDate && (
                  <div>
                    <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-500 mb-3 block">Available Times</label>
                    {slotsLoading ? (
                      <div className="py-8 flex justify-center"><Loader2 className="text-[#D4AF37] animate-spin" /></div>
                    ) : slots.length === 0 ? (
                      <p className="text-zinc-600 font-mc-b text-sm text-center py-8">No available times on this date.</p>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                        {slots.map(slot => (
                          <button key={slot.start_time} onClick={() => { setSelectedSlot(slot); setStep(3); }} className={`py-3 px-2 rounded-lg font-mc-b text-xs transition-all ${selectedSlot?.start_time === slot.start_time ? "bg-[#D4AF37] text-[#0A0A0A]" : "border border-white/10 text-zinc-400 hover:border-[#D4AF37]/30 hover:text-[#F5F5F0]"}`} data-testid={`slot-${slot.start_time}`}>
                            {slot.display}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <BackBtn onClick={() => setStep(selectedFormat ? 1 : 0)} />
              </StepContainer>
            )}

            {step === 3 && (
              <StepContainer key="s3">
                <h2 className="font-mc-h text-2xl text-[#F5F5F0] mb-6 text-center">Tell Us About You</h2>
                <div className="space-y-4">
                  <FormField label="Full Legal Name *" value={form.client_name} onChange={v => setForm({ ...form, client_name: v })} icon={<User size={14} />} required />
                  <FormField label="Preferred Name" value={form.preferred_name} onChange={v => setForm({ ...form, preferred_name: v })} />
                  <FormField label="Email Address *" value={form.client_email} onChange={v => setForm({ ...form, client_email: v })} type="email" icon={<Mail size={14} />} required />
                  <FormField label="Phone Number" value={form.client_phone} onChange={v => setForm({ ...form, client_phone: v })} icon={<Phone size={14} />} />
                  <FormField label="How did you hear about us?" value={form.referral_source} onChange={v => setForm({ ...form, referral_source: v })} />
                  <FormField label="What brings you to a Mirror Conversation?" value={form.reason_for_booking} onChange={v => setForm({ ...form, reason_for_booking: v })} textarea />
                  <FormField label="What are you hoping to get from this session?" value={form.hopes} onChange={v => setForm({ ...form, hopes: v })} textarea />
                  <FormField label="Any accessibility or accommodation needs?" value={form.accessibility_needs} onChange={v => setForm({ ...form, accessibility_needs: v })} />
                </div>
                <div className="flex justify-between mt-8">
                  <BackBtn onClick={() => setStep(2)} inline />
                  <button onClick={() => { if (!form.client_name || !form.client_email) { toast.error("Name and email required."); return; } setStep(4); }} className="bg-[#D4AF37] text-[#0A0A0A] px-8 py-3 rounded-full font-mc-b text-sm uppercase tracking-wide flex items-center gap-2 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all" data-testid="continue-to-confirm">
                    Continue <ArrowRight size={14} />
                  </button>
                </div>
              </StepContainer>
            )}

            {step === 4 && (
              <StepContainer key="s4">
                <h2 className="font-mc-h text-2xl text-[#F5F5F0] mb-6 text-center">Confirm Your Booking</h2>
                <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-4 mb-8">
                  <SummaryRow label="Service" value={selectedService?.title} />
                  <SummaryRow label="Duration" value={`${selectedService?.duration} minutes`} />
                  <SummaryRow label="Format" value={selectedFormat === "office" ? "In Office" : "Zoom"} />
                  <SummaryRow label="Date" value={new Date(selectedDate + "T12:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })} />
                  <SummaryRow label="Time" value={selectedSlot?.display} />
                  <SummaryRow label="Name" value={form.client_name} />
                  <SummaryRow label="Email" value={form.client_email} />
                  {selectedFormat === "office" && <SummaryRow label="Location" value={settings?.office_address} />}
                  {selectedService?.price > 0 && (
                    <div className="pt-4 border-t border-white/[0.06]">
                      <SummaryRow label={selectedService.payment_timing === "deposit" ? "Deposit Due" : "Total"} value={`$${selectedService.payment_timing === "deposit" ? selectedService.deposit_amount : selectedService.price}`} gold />
                      {selectedService.payment_timing === "free" && <SummaryRow label="Cost" value="Complimentary" gold />}
                    </div>
                  )}
                </div>

                {selectedService?.cancellation_policy && (
                  <p className="font-mc-b text-xs text-zinc-600 mb-4">{selectedService.cancellation_policy}</p>
                )}

                <p className="font-mc-b text-xs text-zinc-600 text-center mb-6 leading-relaxed">
                  Mirror Conversations&trade; is not therapy, counseling, or mental health treatment. By booking, you acknowledge this is a personal development service.
                </p>

                <div className="flex justify-between">
                  <BackBtn onClick={() => setStep(3)} inline />
                  <button onClick={handleSubmit} disabled={submitting} className="bg-[#D4AF37] text-[#0A0A0A] px-10 py-4 rounded-full font-mc-b text-sm uppercase tracking-wide flex items-center gap-2 hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transition-all disabled:opacity-50" data-testid="confirm-booking">
                    {submitting ? <Loader2 size={16} className="animate-spin" /> : <Calendar size={16} />}
                    {submitting ? "Booking..." : selectedService?.price > 0 && selectedService?.payment_timing !== "free" ? "Book & Pay" : "Confirm Booking"}
                  </button>
                </div>
              </StepContainer>
            )}
          </AnimatePresence>
        </div>
      </PageWrapper>
    </Layout>
  );
}

function PageWrapper({ children }) {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        .font-mc-h { font-family: 'Cormorant Garamond', serif; }
        .font-mc-b { font-family: 'DM Sans', sans-serif; }
      `}</style>
      <div className="min-h-screen bg-[#0A0A0A] pt-24 pb-20 px-6">{children}</div>
    </>
  );
}

function StepContainer({ children }) {
  return <motion.div initial="hidden" animate="visible" exit="hidden" variants={fadeUp}>{children}</motion.div>;
}

function BackBtn({ onClick, inline }) {
  return (
    <button onClick={onClick} className={`flex items-center gap-2 text-zinc-600 hover:text-[#D4AF37] font-mc-b text-xs uppercase tracking-wide transition-colors ${inline ? "" : "mt-8"}`}>
      <ArrowLeft size={14} /> Back
    </button>
  );
}

function FormField({ label, value, onChange, type = "text", textarea, icon, required }) {
  const Tag = textarea ? "textarea" : "input";
  return (
    <div>
      <label className="font-mc-b text-xs tracking-[0.15em] uppercase text-zinc-500 mb-2 block">{label}</label>
      <div className="relative">
        {icon && <div className="absolute left-0 top-1/2 -translate-y-1/2 text-zinc-600">{icon}</div>}
        <Tag type={type} value={value} onChange={e => onChange(e.target.value)} required={required} rows={textarea ? 3 : undefined}
          className={`w-full bg-transparent border-b border-white/10 text-[#F5F5F0] font-mc-b py-3 focus:border-[#D4AF37] focus:outline-none transition-colors ${icon ? "pl-6" : ""} ${textarea ? "resize-none" : ""}`} />
      </div>
    </div>
  );
}

function SummaryRow({ label, value, gold }) {
  return (
    <div className="flex justify-between items-start">
      <span className="font-mc-b text-xs text-zinc-500 uppercase tracking-wider">{label}</span>
      <span className={`font-mc-b text-sm text-right max-w-[60%] whitespace-pre-line ${gold ? "text-[#D4AF37] font-medium" : "text-[#F5F5F0]"}`}>{value}</span>
    </div>
  );
}
